import { inject, Injectable } from '@angular/core';
import { concat, defer, map, Observable, of, startWith, tap } from 'rxjs';
import { concatMap } from 'rxjs/operators';

import { BescheidLoadPhase } from '../enums/bescheid-load-phase.enum';
import { isComplete } from '../enums/load-state.enum';
import { MimeTypes } from '../enums/mime-types.enum';
import { BescheidLoad } from '../models/BescheidLoad';
import { EndToEndEncrypter } from '../providers/crypto/end-to-end-encrypter.service';
import { LogService } from '../providers/log/log.service';
import { ResourceProvider } from '../providers/resource/resource';
import { PhaseTimings } from './phase-timings';
import { ZipService } from './zip.service';

const LOG_HEADING = 'bescheid-loader';
const LOG_READY = 'Bescheid ready';
const LOG_FAILED = 'Bescheid failed';

/**
 * Loads a Bescheid: download, decrypt, unpack.
 *
 * App and portal run the same sequence and should report the same phases, which is why it lives
 * here instead of twice in the detail pages. Preparing the files for the respective view stays with
 * the callers — the views differ, the load does not.
 */
@Injectable({ providedIn: 'root' })
export class BescheidLoaderService {
  private readonly encrypter = inject(EndToEndEncrypter);
  private readonly resource = inject(ResourceProvider);
  private readonly zip = inject(ZipService);

  /**
   * @param {string} rGuid - The rGuid of the Bescheid.
   * @return {Observable<BescheidLoad>} Phase and progress updates, finally the files.
   */
  public load(rGuid: string): Observable<BescheidLoad> {
    return defer(() => {
      const timings = new PhaseTimings();

      return this.resource.getBescheidBlobByIdWithProgress(rGuid).pipe(
        concatMap((download) => {
          if (!isComplete(download)) {
            return of(timings.downloading(download.loadedBytes, download.totalBytes));
          }
          // The response carries the authoritative size; progress events may never have arrived.
          timings.downloaded(download.body.size);

          return this.decryptAndExtract(download.body, timings);
        }),
        startWith(timings.enter(BescheidLoadPhase.Downloading)),
        tap({
          complete: () => LogService.info(`${LOG_READY} — ${timings.render()}`, false, LOG_HEADING),
          error: () => LogService.error(`${LOG_FAILED} — ${timings.render()}`, false, LOG_HEADING),
        }),
      );
    });
  }

  private decryptAndExtract(blob: Blob, timings: PhaseTimings): Observable<BescheidLoad> {
    // Every phase is announced *before* its work starts — otherwise the view keeps showing the
    // previous step while the work runs. Each piece of work therefore sits in a defer that only
    // executes after the announcement.
    return concat(
      defer(() => of(timings.enter(BescheidLoadPhase.Decrypting))),
      defer(() => this.encrypter.decryptToUint8(blob)).pipe(
        concatMap((decrypted) =>
          concat(
            defer(() => of(timings.enter(BescheidLoadPhase.Extracting))),
            defer(() => this.zip.unzip(new Blob([decrypted], { type: MimeTypes.ZIP }))).pipe(
              map((files) => timings.done(files)),
            ),
          ),
        ),
      ),
    );
  }
}
